/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.footerContent = this.querySelector('.js-footer-content');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      // HIG-110: tracks whether this drawer was opened FROM the cart, which decides
      // what the top dismiss control does (back-to-cart vs close).
      this.fromCart = false;
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      // HIG-101: delegate the "back to cart" click on the drawer itself so it also
      // works for the BOTTOM back button, which is part of the fetched product
      // content (not in the DOM when the constructor ran). The static header arrow
      // lives inside the drawer too, so this one listener covers both.
      this.addEventListener('click', (evt) => {
        if (evt.target.closest('.js-open-cart-drawer')) this.handleOpenCartClick(evt);
      });

      // HIG-106: delegate the drawer-only "Benachrichtige mich" button. The button is
      // part of the fetched footer content, so delegation on the drawer covers it after
      // each render. Opens STOQ's back-in-stock popup via its JS API.
      this.addEventListener('click', (evt) => {
        if (evt.target.closest('.js-quickadd-notify')) this.handleNotifyClick(evt);
      });
      // HIG-110: the "Schliessen" controls (.js-close-drawer) are handled by SideDrawer's
      // own click handler; the "Zurück zum Warenkorb" controls (.js-open-cart-drawer) by
      // the delegation above. Their visibility is set per entry context in
      // updateBackVisibility(). No extra wiring needed here.

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page. Re-query in case the
      // <cart-drawer> wasn't in the DOM when this drawer initialised.
      const cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');
      if (cartDrawer) {
        evt.preventDefault();
        // Close this quick-add drawer first, otherwise the cart drawer opens
        // *behind* it and nothing appears to happen (the "Zurück zum Warenkorb"
        // back button looked broken). 200ms matches the close animation.
        this.close();
        setTimeout(() => cartDrawer.open(), 200);
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * HIG-110: Show every "Zurück zum Warenkorb" control (header arrow, mobile bar arrow,
     * bottom button) only when the drawer was opened FROM the cart. The "Schliessen"
     * controls are always visible. Called on every render (incl. the same-product
     * early-open path, which skips renderProduct).
     */
    updateBackVisibility() {
      this.querySelectorAll('.js-back-to-cart').forEach((el) => {
        el.hidden = !this.fromCart;
      });
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.fromCart = true; // HIG-110: dismiss control returns to the cart
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.fromCart = false; // HIG-110: dismiss control closes the drawer
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // HIG-106: toggle the back-in-stock notify UI for the newly selected variant.
      this.updateNotifyState(evt.detail.variant);

      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        this.updateMedia(evt.detail.variant.featured_media);
      }

      // HIG-124: refresh the delivery line for the newly selected variant.
      this.renderDelivery(evt.detail.variant ? evt.detail.variant.id : null);
    }

    /**
     * HIG-124: render the compact delivery line for the selected variant in the
     * Choose-Options drawer, identical to the category cards. Reads the product's
     * logistics.variants_data (emitted into the drawer template) + the shared
     * renderer from hp-collection-card.js (loaded on the grid pages where the
     * drawer opens). Gracefully hides if either is absent.
     * @param {?(number|string)} variantId - Selected variant id, or null for the server default.
     */
    renderDelivery(variantId) {
      const root = this.content.querySelector('[data-hp-qa-delivery]');
      if (!root) return;
      const api = window.HPDeliveryCore;
      const dataEl = this.content.querySelector('[data-hp-qa-vd]');
      if (!api || typeof api.renderCompact !== 'function' || !dataEl) { root.hidden = true; return; }
      let vd;
      try { vd = JSON.parse(dataEl.textContent); } catch (e) { root.hidden = true; return; }
      const vid = String(variantId || root.getAttribute('data-hp-qa-current') || '');
      const entry = vd && vid ? vd[vid] : null;
      // Härtung 2026-07-30 (Rehband-Vorfall): FEHLENDER Eintrag = keine Daten,
      // NICHT ausverkauft — Zeile verstecken statt falschen OOS-Text zeigen.
      if (!entry) { root.hidden = true; return; }
      const breakdown = Array.isArray(entry.stock_breakdown) ? entry.stock_breakdown : [];
      // Auslaufend-Flag (variants_data._disc): ausverkaufte Varianten zeigen im
      // Drawer «Ausverkauft» statt «Ausverkauft, Nachschub geplant» (Tobias 2026-07-30).
      root.innerHTML = api.renderCompact(breakdown, { discontinued: !!(vd && vd._disc === 1) });
      root.hidden = false;
    }

    /**
     * HIG-122 fix: merge THIS product's real per-bucket quantities (logistics.vb,
     * emitted as [data-hp-qa-vb] in the drawer template) into window.__hpb. Products
     * opened via the Choose-Options drawer (sliders / SliderCart recommendations) are
     * otherwise not in __hpb, so the cart annotator (warehouse-cart-reconcile.js)
     * couldn't set their Verfügbarkeit line-item property. Keyed by variant id, same
     * shape as hp-split-data.liquid; persists for the session so qty changes work too.
     */
    mergeHpb() {
      const el = this.content.querySelector('[data-hp-qa-vb]');
      if (!el) return;
      try {
        const vb = JSON.parse(el.textContent);
        if (vb && typeof vb === 'object' && !Array.isArray(vb)) {
          window.__hpb = Object.assign(window.__hpb || {}, vb);
        }
      } catch (e) {}
    }

    /**
     * HIG-106: Reads the server-selected variant from the freshly rendered drawer
     * content (the `current_variant | json` script the variant-picker emits).
     * @returns {?object} Variant object ({ id, available, … }) or null.
     */
    getRenderedCurrentVariant() {
      const el = this.content.querySelector('variant-picker [type="application/json"]:not([data-all-variants])');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    /**
     * HIG-106: Move the notify button into the product form, right after the add-to-cart
     * row, so CSS can lay out quantity (35%) + notify (65%) on one line like the PDP.
     * Idempotent — only moves it when it isn't already inside the form.
     */
    placeNotifyButton() {
      const fc = this.footerContent;
      if (!fc) return;
      const form = fc.querySelector('.js-product-form-main');
      const notify = fc.querySelector('.js-quickadd-notify');
      const addRow = form && form.querySelector('.product-info__add-to-cart');
      if (form && notify && addRow && notify.parentElement !== form) {
        addRow.insertAdjacentElement('afterend', notify);
      }
    }

    /**
     * HIG-106: Toggle the drawer's back-in-stock notify UI. When the selected variant
     * is sold out we swap the dead "Ausverkauft" cart row for our notify button
     * (CSS keyed on .is-notify-active in swatches-styles.css). Remembers the variant id
     * so the notify click can pass it to STOQ.
     * @param {?object} variant - The selected variant, or null if none resolved.
     */
    updateNotifyState(variant) {
      const soldOut = !!(variant && variant.available === false);
      if (this.footerContent) this.footerContent.classList.toggle('is-notify-active', soldOut);
      this.currentVariantId = variant ? variant.id : null;
    }

    /**
     * HIG-106: Open STOQ's back-in-stock popup for the selected sold-out variant via
     * its documented JS API (window._RestockRocket.openModal). STOQ doesn't inject its
     * own notify button inside the quick-add drawer, so this is the drawer's entry point.
     * @param {object} evt - Event object.
     */
    handleNotifyClick(evt) {
      evt.preventDefault();
      const rr = window._RestockRocket;
      if (!rr || typeof rr.openModal !== 'function' || !this.currentVariantId) return;

      const jsonEl = this.querySelector('.js-quickadd-product-json');
      let productData = null;
      try {
        productData = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      } catch (e) {
        productData = null;
      }
      if (!productData) return;

      rr.openModal(productData, this.currentVariantId);
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      // HIG-109: the body scroll lock is handled centrally by the drawer-scroll-lock
      // setup at the bottom of this file (reacts to both the cart + quick-add drawers).

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        // HIG-110: renderProduct is skipped here, so refresh the back-control visibility
        // (the SAME product can be opened from a slider one time and the cart the next).
        this.updateBackVisibility();
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.getElementById('quick-buy-template').content.querySelector('.js-product');
        this.renderProduct(opener);
      }

      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      // HIG-109: scroll lock is released centrally (see drawer-scroll-lock setup) only
      // once NO drawer is open — so returning to the cart keeps the page locked.
      super.close(() => {
        this.backBtn.hidden = true;
        this.fromCart = false; // HIG-110: reset entry context for the next open
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      QuickAddDrawer.convertToQuickBuyContent(this.productEl, this.productUrl);

      // Check whether to select the first variant.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      // HIG-101: the bottom "Zurück zum Warenkorb" button only makes sense when the
      // drawer was opened FROM the cart. Set on productEl BEFORE injection (no flash);
      // updateBackVisibility() below also syncs the live header + mobile-bar controls.
      const bottomBack = this.productEl.querySelector('.js-back-to-cart');
      if (bottomBack) bottomBack.hidden = !this.fromCart;

      this.updateFooter();
      this.updateContent();
      this.mergeHpb(); // HIG-122 fix: feed this product's real buckets into window.__hpb

      // HIG-106: move the notify button INTO the form (next to the quantity) so the
      // sold-out swap mirrors the PDP — quantity stays, only the button slot changes.
      this.placeNotifyButton();

      // HIG-110: show the "Zurück zum Warenkorb" controls only when opened from the cart.
      this.updateBackVisibility();

      // HIG-106: the variant-picker only fires on:variant:change on user-driven changes,
      // not on initial render, so seed the notify state from the server-selected variant.
      this.updateNotifyState(this.getRenderedCurrentVariant());

      // HIG-124: initial delivery line (variant-picker doesn't fire on:variant:change
      // on first render, mirror the notify-state seeding above).
      this.renderDelivery((this.getRenderedCurrentVariant() || {}).id);

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} media - Variant featured media object.
     */
    updateMedia(media) {
      if (!media) return;

      const { src } = media.preview_image;
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = media.preview_image.aspect_ratio;

      const existingId = container.dataset.mediaId;
      const existingImg = container.querySelector('img');

      if (!existingImg || existingId !== `${media.id}`) {
        container.innerHTML = `
          <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${media.alt || ''}">
        `;
        container.dataset.mediaId = media.id;
      }
    }

    /**
     * Renders the markup for the drawer content element.
     */
    updateContent() {
      const productContent = this.productEl.querySelector('.js-product-details');
      if (productContent) {
        this.content.innerHTML = productContent.innerHTML;
      }

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Renders the markup for the drawer footer element.
     */
    updateFooter() {
      const footerContent = this.productEl.querySelector('.js-footer-content');
      this.footer.classList.remove('quick-add__footer-message');

      if (footerContent && footerContent.hasChildNodes()) {
        this.footerContent.innerHTML = footerContent.innerHTML;
      } else {
        this.footer.classList.add('quick-add__footer-message');
        this.footerContent.innerHTML = `
          <div class="alert quick-add__alert bg-info-bg text-info-text">
            <div class="flex">
              <div>
                <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                  <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
              </div>
            </div>
          </div>`;
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }

    /**
     * Convert product template content to quick buy content.
     * @param {Element} container - Container element containing new content to use.
     */
    static convertToQuickBuyContent(container) {
      // Prevent variant picker from updating the URL on change.
      const variantPicker = container.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = container.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);

  window.theme = window.theme || {};
  window.theme.quickBuy = window.theme.quickBuy || {};
  window.theme.quickBuy.convertToQuickBuyContent = QuickAddDrawer.convertToQuickBuyContent;

  /**
   * HIG-109: robust scroll lock for the cart + quick-add drawers. SideDrawer.open() never
   * locks the page, so on iOS the touch-scroll bleeds through to the page behind the
   * drawer — most visibly AFTER returning from the choose-options screen to the cart (the
   * cart was never locked). Centralised here via the drawers' own before-open/after-close
   * events, so neither drawer's code has to change. The body is pinned with position:fixed
   * (the only reliable iOS lock) preserving the scroll position, and released only once NO
   * drawer is open — so the quick-add → cart hand-off stays locked the whole time.
   */
  if (!window.__hpDrawerScrollLock) {
    window.__hpDrawerScrollLock = true;
    let lockedY = 0;
    const anyDrawerOpen = () => !!document.querySelector(
      'cart-drawer[aria-hidden="false"], quick-add-drawer[aria-hidden="false"]'
    );
    const lock = () => {
      if (document.documentElement.classList.contains('drawer-scroll-locked')) return;
      lockedY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = `-${lockedY}px`;
      document.documentElement.classList.add('drawer-scroll-locked');
    };
    const unlock = () => {
      if (!document.documentElement.classList.contains('drawer-scroll-locked')) return;
      document.documentElement.classList.remove('drawer-scroll-locked');
      document.body.style.top = '';
      window.scrollTo(0, lockedY);
    };
    // Unlock as soon as the drawer is actually hidden — NOT when the slow `after-close`
    // event fires (~600ms = full close transition). Binding only to after-close left the
    // body pinned ~600ms after the drawer had already closed → "can't scroll for 1-2s
    // after closing the cart" (2026-06-09). `before-close` fires at close() start and
    // aria-hidden flips to true synchronously right after, so a short deferred re-check
    // sees the closed state. The 300ms delay is kept ABOVE the quick-add→cart hand-off
    // open (200ms in CartDrawer.open) AND any pending unlock is cancelled when a drawer
    // (re)opens (lockAndHold) — so a hand-off never briefly unlocks (preserving the
    // iOS bleed-through fix this whole block exists for).
    let unlockTimer = null;
    const scheduleUnlock = () => {
      clearTimeout(unlockTimer);
      unlockTimer = setTimeout(() => { if (!anyDrawerOpen()) unlock(); }, 300);
    };
    const lockAndHold = () => { clearTimeout(unlockTimer); lock(); };
    document.addEventListener('on:cart-drawer:before-open', lockAndHold);
    document.addEventListener('on:quickbuy:before-open', lockAndHold);
    document.addEventListener('on:cart-drawer:before-close', scheduleUnlock);
    document.addEventListener('on:quickbuy:before-close', scheduleUnlock);
    // Backstops (no-op if already unlocked): the slow close event + bfcache restore.
    document.addEventListener('on:cart-drawer:after-close', scheduleUnlock);
    document.addEventListener('on:quickbuy:after-close', scheduleUnlock);
  }
}
